import {
  ChangeDetectionStrategy,
  Component,
  inject,
  ViewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { DatePicker } from 'primeng/datepicker';
import { DialogService } from 'primeng/dynamicdialog';
import { InputText } from 'primeng/inputtext';
import { Paginator } from 'primeng/paginator';
import { Select } from 'primeng/select';
import { Table, TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { APP_PATHS } from '../../../../../core/config/app-paths';
import { TableBaseAbstractComponent } from '../../../../../shared/components/table-base-abstract.component';
import { ApplicationFormHistoryEventObject } from '../../../../../shared/enums/application-form-history-event-object.enum';
import { ApplicationFormHistoryEventType } from '../../../../../shared/enums/application-form-history-event-type.enum';
import { ApplicationFormHistory } from '../../../../../shared/models/application-form-history';
import { TableRequestData } from '../../../../../shared/models/table/table-request-param';
import { TableResponse } from '../../../../../shared/models/table-response';
import { ApplicationFormsApi } from '../../../../../shared/services/api/application-forms.api';
import { ApplicationUtils } from '../../../../../shared/utils/application.utils';
import { ApplicationHistoryEntryDetailsDialog } from '../application-history-entry-details-dialog/application-history-entry-details-dialog';

@Component({
  selector: 'app-application-history',
  imports: [
    Button,
    Card,
    DatePipe,
    DatePicker,
    FormsModule,
    InputText,
    Paginator,
    Select,
    TableModule,
    Tooltip,
  ],
  templateUrl: './application-history.component.html',
  styleUrl: './application-history.component.scss',
  providers: [ApplicationFormsApi],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplicationHistoryComponent extends TableBaseAbstractComponent<ApplicationFormHistory> {
  @ViewChild('table') override tableRef!: Table;

  readonly #applicationFormsApi = inject(ApplicationFormsApi);
  readonly #route = inject(ActivatedRoute);
  readonly #router = inject(Router);
  readonly #dialogService = inject(DialogService);
  readonly #applicationFormId: number;

  protected tableFilters = {
    eventDate: null as Date | null,
    eventObject: null as ApplicationFormHistoryEventObject | null,
    eventType: null as ApplicationFormHistoryEventType | null,
    eventAuthor: '',
  };

  protected readonly eventObjectOptions = [
    {
      label: ApplicationFormHistoryEventObject.APPLICATION_FORM,
      value: ApplicationFormHistoryEventObject.APPLICATION_FORM,
    },
  ];

  protected readonly eventTypeOptions = [
    {
      label: 'Modyfikacja',
      value: ApplicationFormHistoryEventType.MODYFIKACJA,
    },
  ];

  constructor() {
    super();

    this.#applicationFormId = Number(this.#route.snapshot.paramMap.get('id'));
  }

  protected override apiCall$ = (
    params: TableRequestData,
  ): Observable<TableResponse<ApplicationFormHistory>> => {
    return this.#applicationFormsApi.getApplicationFormHistoryTable$(
      this.#applicationFormId,
      params,
    );
  };

  protected onFilterChangeMobile(
    target: EventTarget | null,
    field: string,
  ): void {
    this.onPrimeFilterChangeMobile(target as any, field);
  }

  protected showDetails(entry: ApplicationFormHistory): void {
    this.#dialogService.open(ApplicationHistoryEntryDetailsDialog, {
      header: 'Szczegóły zdarzenia',
      closable: true,
      modal: true,
      closeOnEscape: true,
      dismissableMask: true,
      width: '900px',
      breakpoints: {
        '960px': '90vw',
        '640px': '90vw',
      },
      inputValues: { event: entry },
    });
  }

  protected clearFilters(): void {
    this.tableFilters = {
      eventDate: null,
      eventObject: null,
      eventType: null,
      eventAuthor: '',
    };
    this.resetFilters();
  }

  protected close(): void {
    this.#router.navigate([
      `/${APP_PATHS.admin.root}/${APP_PATHS.admin.applications}`,
    ]);
  }

  protected readonly ApplicationUtils = ApplicationUtils;
}
